// askSmart-ui/src/pages/TOSP/tenantStatement.ts
//
// A tenancy's statement when a space changes hands.
//
// THE BUDGET DOES NOT MOVE. It charges whoever held a unit when the year
// opened, for all twelve months, and that is what the revenue sheet shows and
// what was keyed into QuickBooks. What moves is the DISPLAY: a tenancy that
// leaves in June is shown six months, and the tenancy that takes the space on
// is shown the rest — the same figures, divided by who actually held the space.
// The difference between the two is settled at the year-end reconciliation.
//
// ONE RULE DOES BOTH SIDES. A tenancy's recoveries are the unit's own budget
// figure multiplied by their share of each month, so the leaver's months after
// exit come to nothing and the arrival's months before commencement come to
// nothing, without either case being written out.

import { possessionOf } from "./budgetAllocation";
import { HST_RATE } from "./portal/tenantExpenses";
import {
  BudgetRevenueLine,
  BudgetStatementSnapshot,
  occupancyDayShare,
  Tenant,
  tenantLabel,
  Unit,
} from "./types";

const round2 = (value: number): number => Math.round(value * 100) / 100;
const zeroes = (): number[] => new Array(12).fill(0);
const sum = (values: number[]): number => values.reduce((a, b) => a + b, 0);

/** unitId → twelve monthly figures. */
export type PerUnitMonths = Map<string, number[]>;

/**
 * Everything a split statement is built from, for one budget year.
 *
 * Assembled once for the whole portfolio and read per tenancy — the per-unit
 * CAM alone divides eleven categories across ninety-odd units, which is not
 * work to repeat sixty times.
 */
export interface StatementBasis {
  year: number;
  /**
   * The budget's charges BY UNIT: what a handover divides.
   *
   * The royalty is in here with the recoveries, though BMVA charges it on a
   * tenancy's sales rather than on a space. An arrival is charged what the
   * budget expected of the tenancy before them — your instruction — and
   * spreading that tenancy's budgeted royalty across its units by area is what
   * makes that one rule instead of a special case. For a tenancy that held its
   * units all year it adds back up to exactly their own budgeted royalty.
   */
  perUnit: {
    cam: PerUnitMonths;
    taxes: PerUnitMonths;
    dues: PerUnitMonths;
    royalty: PerUnitMonths;
  };
  /** unitId → the tenancy the budget charged for it, the holder at 1 January. */
  incumbents: Map<string, string>;
  /**
   * The budget's own per-tenancy rows, for the two lines that are not borne by
   * a unit: rent and percentage rent.
   */
  revenue: BudgetRevenueLine[];
}

/** Rent as the LEASE states it, for a tenancy the budget never knew about. */
export interface LeaseRent {
  baseRent: number[];
  storageRent: number[];
}

/**
 * One tenancy's hold on one unit: their share of each month, 0–1.
 *
 * READ FROM THEIR OWN LEASES AND NOTHING ELSE, which is what lets a tenant's
 * own portal divide their statement without being given the rest of the
 * centre's tenancies. Two leases cannot overlap on a unit — the takeover
 * wizard ends one the day before the next commences — so a tenancy's own dates
 * already say exactly how much of each month was theirs.
 *
 * Possession, not rent: a tenant fitting out rent-free is still paying for the
 * lights and the snow clearing. Capped at a whole month in case a correction
 * ever leaves two of a tenancy's own leases overlapping.
 */
export function holdOf(unit: Unit, tenant: Tenant, year: number): number[] {
  const months = zeroes();
  for (const occupancy of tenant.occupancies || []) {
    for (const space of occupancy.leasedSpaces || []) {
      if (space.unitId !== unit.id) continue;
      for (let m = 0; m < 12; m += 1) {
        months[m] = Math.min(
          1,
          months[m] + occupancyDayShare(occupancy, possessionOf(occupancy, space), year, m + 1),
        );
      }
    }
  }
  return months;
}

/** Every unit a tenancy held at any point in the year. */
function unitsOf(tenant: Tenant, units: Unit[]): Unit[] {
  const ids = new Set<string>();
  for (const occupancy of tenant.occupancies || []) {
    for (const space of occupancy.leasedSpaces || []) {
      if (space.unitId) ids.add(space.unitId);
    }
  }
  return units.filter((unit) => ids.has(unit.id));
}

const rowFor = (
  revenue: BudgetRevenueLine[],
  key: string,
  tenantId: string,
): number[] | null => {
  const line = revenue.find((candidate) => candidate.key === key);
  const row = line?.contributions.find((candidate) => candidate.id === tenantId);
  return row ? [...row.months] : null;
};

/**
 * Whether this tenancy's year needs dividing at all.
 *
 * True when a space it holds changed hands during the year, or when it did not
 * hold one of its spaces for the whole year — a leaver, an arrival, or a unit
 * taken over. Everybody else reads the budget's own row for their tenancy
 * (`budgetStatement`), which keeps any figure keyed on the revenue sheet.
 *
 * So the split path is taken only where there is something to split, and the
 * fifty-odd unchanged tenancies are untouched by it.
 */
export function changedHands(tenant: Tenant, year: number, units: Unit[]): boolean {
  const mine = unitsOf(tenant, units);
  if (mine.length === 0) return false;
  return mine.some((unit) => holdOf(unit, tenant, year).some((share) => share < 0.999));
}

/**
 * One tenancy's statement for a budget year, divided by who held each space.
 *
 * Null where the tenancy carries nothing at all: the only lines left would be
 * HST and the total, which is not a statement.
 */
export function splitStatement(args: {
  tenant: Tenant;
  basis: StatementBasis;
  units: Unit[];
  /** Rent from this tenancy's own lease, for an arrival the budget never saw. */
  leaseRent: LeaseRent;
  /** Area and the per-unit tax rates; the CAM rate is derived here. */
  rates: { sqFt: number | null; taxes: BudgetStatementSnapshot["perSqFt"]["taxes"] };
}): BudgetStatementSnapshot | null {
  const { tenant, basis, units, leaseRent, rates } = args;
  const mine = unitsOf(tenant, units);

  const cam = zeroes();
  const taxes = zeroes();
  const dues = zeroes();
  const royalty = zeroes();
  /** Months this tenancy held any of its spaces, which is what rent follows. */
  const occupied = new Array(12).fill(false);

  for (const unit of mine) {
    const hold = holdOf(unit, tenant, basis.year);

    for (let m = 0; m < 12; m += 1) {
      if (hold[m] <= 0) continue;
      occupied[m] = true;
      // THE UNIT'S FIGURE, THEIR SHARE OF THE MONTH. Pro-rated to the day,
      // because the takeover wizard ends one lease the day before the next
      // commences — so the two shares of the boundary month come to exactly
      // one, and between them they bill what the budget charged.
      cam[m] += (basis.perUnit.cam.get(unit.id)?.[m] || 0) * hold[m];
      taxes[m] += (basis.perUnit.taxes.get(unit.id)?.[m] || 0) * hold[m];
      dues[m] += (basis.perUnit.dues.get(unit.id)?.[m] || 0) * hold[m];
      royalty[m] += (basis.perUnit.royalty.get(unit.id)?.[m] || 0) * hold[m];
    }
  }

  // ── Rent ────────────────────────────────────────────────────────────────
  //
  // From the budget where the budget knows this tenancy, so a figure keyed on
  // the revenue sheet is what the tenant sees — but only for the months they
  // actually held the space. The budget gives an incumbent twelve months
  // whether their lease runs out in June or not; their statement must not.
  //
  // An arrival the budget never saw has no row to read, so their rent comes
  // from their own lease. Rent is not a recovery: there is nothing to divide,
  // the lease simply states what is owed.
  const budgetedBase = rowFor(basis.revenue, "baseRent", tenant.id);
  const budgetedStorage = rowFor(basis.revenue, "storageRent", tenant.id);
  const held = (months: number[] | null, fallback: number[]): number[] =>
    (months || fallback).map((amount, m) => (occupied[m] ? amount : 0));
  const baseRent = held(budgetedBase, leaseRent.baseRent);
  const storageRent = held(budgetedStorage, leaseRent.storageRent);

  // ── Percentage rent ─────────────────────────────────────────────────────
  //
  // NOT INHERITED, and nil for an arrival. It is a share of a tenant's own
  // takings above their own breakpoint, so what the budget expected of their
  // predecessor says nothing whatever about it. A leaver keeps theirs up to
  // the month they went, like every other line.
  const percentageRent = (rowFor(basis.revenue, "percentageRent", tenant.id) || zeroes()).map(
    (amount, m) => (occupied[m] ? amount : 0),
  );

  const charges: BudgetStatementSnapshot["lines"] = [];
  const put = (key: string, label: string, months: number[]): void => {
    // A line the tenancy carries nothing on does not belong on their
    // statement: a row of nil reads as a charge nobody can account for.
    if (!months.some((amount) => Math.abs(amount) > 0.005)) return;
    const rounded = months.map(round2);
    charges.push({ key, label, months: rounded, total: round2(sum(rounded)) });
  };

  put("baseRent", "Base Rent", baseRent);
  put("storageRent", "Storage Rent", storageRent);
  put("propertyTaxes", "Property Taxes", taxes);
  put("cams", "CAMS", cam);
  put("bmvaBase", "BMVA Base Fee", dues);
  put("bmvaRoyalty", "BMVA Royalty", royalty);
  put("percentageRent", "Percentage Rent", percentageRent);

  if (charges.length === 0) return null;

  const hst = zeroes();
  const total = zeroes();
  for (let m = 0; m < 12; m += 1) {
    const known = charges
      .map((line) => line.months[m])
      .filter((amount): amount is number => amount !== null);
    if (known.length === 0) continue;
    const subtotal = known.reduce((a, b) => a + b, 0);
    hst[m] = round2(subtotal * HST_RATE);
    total[m] = round2(subtotal + hst[m]);
  }

  const lines = [
    ...charges,
    { key: "hst", label: "HST", months: hst, total: round2(sum(hst)) },
    { key: "total", label: "Total", months: total, total: round2(sum(total)), emphasis: true },
  ];

  const cams = lines.find((line) => line.key === "cams")?.total ?? null;
  return {
    tenantId: tenant.id,
    label: tenantLabel(tenant),
    lines,
    perSqFt: {
      sqFt: rates.sqFt,
      cams: cams === null || !rates.sqFt ? null : round2(cams / rates.sqFt),
      taxes: rates.taxes,
    },
  };
}
